import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { LazyLoadEvent, MessageService, TableState } from 'primeng/api';
import { Subscription } from 'rxjs';
import { SERVICE_ENDPOINTS } from '../../../core/configs/service-endpoints';
import { ServiceResult } from '../../../core/interfaces/service-result';
import { FileService } from '../../../core/services/file.service';
import { I18nService } from '../../../core/services/i18n.service';
import { RuntimeConfigService } from '../../../core/services/runtime-config.service';
import { UserSessionSettingsProvider } from '../../../core/services/user-session-settings-provider.service';
import { TableConfig } from '../../../custom/interfaces/sps-table/table-config';
import { TableListProvider } from '../../../custom/provider/table-list-provider';
import { TableConfigService } from '../../../custom/services/table-config.service';
import { TableControlService } from '../../../custom/services/table-control.service';
import { LayoutService } from '../../../layout/services/layout.service';
import { PayoutFileData } from '../../interfaces/payout/payout-file-data';
import { PayoutFilesService } from '../../services/payout-files.service';

@Component({
  template: '',
})
export abstract class BasePayoutFilesComponent implements OnInit {

  public i18n = inject(I18nService);
  public layoutService = inject(LayoutService);
  public tableControlService = inject(TableControlService);
  protected tableConfigService = inject(TableConfigService);
  protected userSessionSettingsProvider = inject(UserSessionSettingsProvider);
  protected payoutFilesService = inject(PayoutFilesService);
  protected messageService = inject(MessageService);
  protected fileService = inject(FileService);
  protected runtimeConfig = inject(RuntimeConfigService);

  /**
   * Table session params
   * @protected
   */
  protected abstract tableConfigKeyName: string;
  protected abstract tableRowsKeyName: string;

  /**
   * table-config for payout-files views
   */
  readonly tableConfig: WritableSignal<TableConfig> = signal(null);
  totalRecords: number = 0;
  rows: number = 20;
  scrollHeightOffset: number = 146;
  minScrollHeight: number = 160;
  tableListProvider: TableListProvider;
  selectedRowData: PayoutFileData = null;

  /**
   * Initialize table config and state
   */
  async ngOnInit() {
    this.tableListProvider = new TableListProvider(this.i18n);
    this.tableConfig.set(await this.tableConfigService.getTableConfig(this.tableConfigKeyName, false));
    this.rows = this.tableConfig().rows;
    if (this.userSessionSettingsProvider.hasValue(this.tableRowsKeyName)) {
      this.rows = this.userSessionSettingsProvider.get(this.tableRowsKeyName);
    }
  }

  /**
   * Parent-Data-Provider
   * @param lazyLoadEvent
   */
  getPayoutFilesPageData = (lazyLoadEvent: LazyLoadEvent) => {
    return this.payoutFilesService.findPage(lazyLoadEvent, this.tableConfig().dataServiceEndpoint);
  };

  /**
   * Table-data-Fetched subscription
   * @param serviceResult
   */
  onDataFetched(serviceResult: ServiceResult) {
    this.totalRecords = serviceResult.totalElements;
  }

  /**
   * row was selected / unselected
   * @param payoutFileData
   */
  onSelectionChanged(payoutFileData: PayoutFileData) {
    this.selectedRowData = null;
    if (payoutFileData) {
      this.selectedRowData = payoutFileData;
    }
  }

  /**
   * Rows changed by user
   * @param newRowCount
   */
  rowsCountChanged(newRowCount: number) {
    this.rows = newRowCount;
    this.userSessionSettingsProvider.set(this.tableRowsKeyName, this.rows);
  }

  /**
   * Table state was restored
   * @protected
   * @param tableState
   */
  protected onStateRestored(tableState: TableState) {
    this.onSelectionChanged(tableState?.selection);
  }

  /**
   * Clear all Table Filters and Sorting
   * @protected
   */
  protected clearTableFilters() {
    this.tableControlService.clearTableFilters.set(true);
  }

  /**
   * create the csv file and download it
   * @protected
   */
  protected downloadPayoutFile(payoutTypeRouteParam: string) {
    const exportUrl: string = this.runtimeConfig.epaDistributionBackend + SERVICE_ENDPOINTS.GET_PAYOUT_CSV + payoutTypeRouteParam + '/' + this.selectedRowData.id;
    const subscription: Subscription = this.fileService.downloadFile(exportUrl).subscribe({
      next: success => {
        subscription.unsubscribe();
        if (success) {
          this.messageService.add({
            life: 3500,
            severity: 'success',
            summary: this.i18n.i18nLng()['FILEDOWNLOADTITLE'],
            detail: this.i18n.i18nLng()['FILEDOWNLOADSUCCESS']
          });
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.fileService?.errorHandler?.message,
            detail: this.fileService?.errorHandler?.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
      }
    });
  }

}
